import React, { useState, useMemo } from 'react';
import { Mafs, Coordinates, Plot, Point, Line, Text, Theme } from 'mafs';
import { Slider } from 'antd';

const formatNum = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '0';
    return Number.isInteger(val) ? String(val) : val.toFixed(2);
};

export const ExponentialGrowthDecay = ({ config }) => {
    const [mode, setMode] = useState(config.mode || 'growth');
    const [initialValue, setInitialValue] = useState(config.initialValue || 1000);
    const [growthRate, setGrowthRate] = useState(config.growthRate || 0.08);
    const [timeRange] = useState(config.timeRange || [0, 20]);
    const [currentTime, setCurrentTime] = useState(0);
    const [showLinear, setShowLinear] = useState(config.showLinear || false);

    const base = useMemo(() => mode === 'growth' ? 1 + growthRate : 1 - growthRate, [mode, growthRate]);

    const doublingTime = useMemo(() => {
        if (base <= 1) return null;
        return Math.log(2) / Math.log(base);
    }, [base]);

    const halfLife = useMemo(() => {
        if (base >= 1) return null;
        return Math.log(0.5) / Math.log(base);
    }, [base]);

    const currentValue = useMemo(() => initialValue * Math.pow(base, currentTime), [initialValue, base, currentTime]);

    const renderGraph = () => {
        const maxY = initialValue * Math.pow(base, timeRange[1]);
        const displayMax = mode === 'growth' ? maxY * 1.1 : initialValue * 1.1;

        return (
            <div style={{ marginTop: '20px' }}>
                <Mafs viewBox={{ x: timeRange, y: [0, displayMax] }} height={400} pan={false}>
                    <Coordinates.Cartesian xAxis={{ lines: 5, labels: (t) => `${Math.round(t)}` }} yAxis={{ lines: true }} />

                    <Plot.OfX y={(t) => initialValue * Math.pow(base, t)} color={Theme.blue} weight={3} />

                    {showLinear && (
                        <Plot.OfX y={(t) => initialValue + initialValue * growthRate * t} color={Theme.green} weight={2} style="dashed" />
                    )}

                    {doublingTime && mode === 'growth' && (
                        <>
                            <Line.Segment point1={[doublingTime, 0]} point2={[doublingTime, initialValue * 2]} color={Theme.orange} style="dashed" />
                            <Point x={doublingTime} y={initialValue * 2} color={Theme.orange} />
                            <Text x={doublingTime + (timeRange[1]*0.05)} y={initialValue * 2}>Double: {doublingTime.toFixed(1)}</Text>
                        </>
                    )}

                    {halfLife && mode === 'decay' && (
                        <>
                            <Line.Segment point1={[halfLife, 0]} point2={[halfLife, initialValue * 0.5]} color={Theme.orange} style="dashed" />
                            <Point x={halfLife} y={initialValue * 0.5} color={Theme.orange} />
                            <Text x={halfLife + (timeRange[1]*0.05)} y={initialValue * 0.5}>Half-Life: {halfLife.toFixed(1)}</Text>
                        </>
                    )}

                    {currentTime > 0 && (
                        <>
                            <Line.Segment point1={[currentTime, 0]} point2={[currentTime, currentValue]} color={Theme.red} weight={2} />
                            <Point x={currentTime} y={currentValue} color={Theme.red} />
                        </>
                    )}
                </Mafs>
            </div>
        );
    };

    return (
        <div className="ch-exponential-widget">
            <div style={{ padding: '15px', background: '#f0f5ff', borderRadius: '8px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 600 }}>
                    {mode === 'growth' ? 'Exponential Growth' : 'Exponential Decay'}
                </h3>
                <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#1890ff' }}>
                    V(t) = V₀ × {mode === 'growth' ? `(1 + r)ᵗ` : `(1 - r)ᵗ`} where V₀ = {formatNum(initialValue)}, r = {formatNum(growthRate)}
                </div>
            </div>

            <div className="ch-controls" style={{ padding: '15px', background: '#fafafa', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                    <button
                        onClick={() => setMode(mode === 'growth' ? 'decay' : 'growth')}
                        style={{ padding: '8px 16px', background: mode === 'growth' ? '#52c41a' : '#ff4d4f', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500, marginRight: '10px' }}
                    >
                        Mode: {mode === 'growth' ? '📈 Growth' : '📉 Decay'}
                    </button>
                    <label style={{ marginLeft: '15px' }}>
                        <input type="checkbox" checked={showLinear} onChange={e => setShowLinear(e.target.checked)} style={{marginRight: '5px'}}/>
                        Show Linear Comparison
                    </label>
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                        Initial Value (V₀): {formatNum(initialValue)}
                    </label>
                    <Slider
                        min={100}
                        max={5000}
                        step={100}
                        value={initialValue}
                        onChange={setInitialValue}
                    />
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                        {mode === 'growth' ? 'Growth' : 'Decay'} Rate (r): {(growthRate * 100).toFixed(1)}%
                    </label>
                    <Slider
                        min={0.01}
                        max={0.25}
                        step={0.01}
                        value={growthRate}
                        onChange={setGrowthRate}
                    />
                </div>

                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                        Time Explorer (t): {formatNum(currentTime)}
                    </label>
                    <Slider
                        min={timeRange[0]}
                        max={timeRange[1]}
                        step={0.5}
                        value={currentTime}
                        onChange={setCurrentTime}
                    />
                </div>
            </div>

            {renderGraph()}

            <div style={{ marginTop: '20px', padding: '20px', background: '#fff7e6', border: '2px solid #ffc53d', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0, color: '#d48806' }}>📊 At time t = {formatNum(currentTime)}</h4>
                <div style={{ lineHeight: '1.8' }}>
                    <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                        V({formatNum(currentTime)}) = {formatNum(initialValue)} × {formatNum(base)}^{formatNum(currentTime)}
                    </p>
                    <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                        V({formatNum(currentTime)}) = {formatNum(initialValue)} × {formatNum(Math.pow(base, currentTime))}
                    </p>
                    <p style={{ margin: '10px 0 5px 0', fontFamily: 'monospace', fontSize: '16px', fontWeight: 'bold', color: '#d48806' }}>
                        V({formatNum(currentTime)}) = {formatNum(currentValue)} {config.valueLabel || 'units'}
                    </p>
                </div>
                {showLinear && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '2px solid #ffe58f' }}>
                        <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px', color: '#52c41a' }}>
                            Linear comparison: {formatNum(initialValue + initialValue * growthRate * currentTime)} {config.valueLabel || 'units'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};